import { useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';
import { experience } from '@/data';
import type { BulletTag } from '@/data/types';
import { formatDuration, formatRange } from '@/lib/dates';
import { TAG_LABELS, orderTags } from '@/lib/tags';
import { cn } from '@/lib/cn';
import { Section } from '@/components/layout/Section';
import { Badge } from '@/components/ui/Badge';
import { Reveal } from '@/components/ui/Reveal';

/** Bullets shown before a role needs expanding. */
const COLLAPSED_COUNT = 4;

export function Experience() {
  const reduceMotion = useReducedMotion();
  const [activeTag, setActiveTag] = useState<BulletTag | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // Chips are derived from the bullets themselves, so a filter can never point
  // at something with nothing behind it.
  const tagCounts = useMemo(() => {
    const counts = new Map<BulletTag, number>();
    for (const role of experience) {
      for (const highlight of role.highlights) {
        for (const tag of highlight.tags) counts.set(tag, (counts.get(tag) ?? 0) + 1);
      }
    }
    return counts;
  }, []);

  const tags = useMemo(() => orderTags(tagCounts), [tagCounts]);

  const toggleTag = (tag: BulletTag) => setActiveTag((current) => (current === tag ? null : tag));

  return (
    <Section
      id="experience"
      title="Experience"
      lead="Filter by the kind of work to see only the relevant lines."
    >
      <div
        className="flex flex-wrap items-center gap-2"
        role="group"
        aria-label="Filter by type of work"
      >
        <FilterChip active={activeTag === null} onClick={() => setActiveTag(null)}>
          All
          <span className="ml-1.5 opacity-60">
            {experience.reduce((n, role) => n + role.highlights.length, 0)}
          </span>
        </FilterChip>

        {tags.map((tag) => (
          <FilterChip key={tag} active={activeTag === tag} onClick={() => toggleTag(tag)}>
            {TAG_LABELS[tag]}
            <span className="ml-1.5 opacity-60">{tagCounts.get(tag)}</span>
          </FilterChip>
        ))}
      </div>

      <ol className="relative mt-9 space-y-10 border-l border-border pl-6 sm:pl-8">
        {experience.map((role, i) => {
          // Education entries close the 2023-2025 date gap. They carry no
          // bullets, so a tag filter cannot apply to them — they stay visible
          // under every filter rather than appearing to have "no matches".
          const isEducation = role.kind === 'education';
          const matching = isEducation
            ? []
            : activeTag
              ? role.highlights.filter((h) => h.tags.includes(activeTag))
              : role.highlights;

          // While filtering, showing a "+N more" toggle would hide matches the
          // visitor explicitly asked for, so the collapse only applies unfiltered.
          const isOpen = expanded[role.id] ?? false;
          const visible = activeTag || isOpen ? matching : matching.slice(0, COLLAPSED_COUNT);
          const hidden = matching.length - visible.length;

          return (
            <li key={role.id} className="relative">
              <span
                aria-hidden
                className={cn(
                  'absolute top-2 -left-[1.6rem] h-3 w-3 rounded-full border-2 border-background transition-colors sm:-left-[2.1rem]',
                  isEducation
                    ? 'bg-muted-foreground'
                    : matching.length > 0
                      ? 'bg-primary'
                      : 'bg-border',
                )}
              />

              <Reveal index={i}>
                <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                  <h3 className="text-lg font-semibold text-foreground">{role.company}</h3>
                  <span className="text-sm text-muted-foreground">
                    {formatRange(role.startDate, role.endDate)}
                    {/* Duration only for finished roles. "Present · 1 yr" reads
                        as a stale counter on a role that is still running, and
                        the tenure of a current job is the one thing a reader
                        can work out themselves. */}
                    {role.endDate !== null ? (
                      <>
                        <span className="mx-1.5 text-border">·</span>
                        {formatDuration(role.startDate, role.endDate)}
                      </>
                    ) : null}
                  </span>
                </div>

                <p className="mt-0.5 text-sm font-medium text-primary">
                  {role.role}
                  {role.client ? (
                    <span className="font-normal text-muted-foreground">
                      {' '}
                      · Client: {role.client}
                    </span>
                  ) : null}
                  <span className="font-normal text-muted-foreground"> · {role.location}</span>
                </p>

                <p className="mt-3 text-pretty text-muted-foreground">{role.summary}</p>

                {role.roles && role.roles.length > 1 ? (
                  <div className="mt-4 rounded-lg border border-border bg-muted/50 px-4 py-3">
                    <p className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                      Progression
                    </p>
                    <ol className="mt-2 space-y-1">
                      {role.roles.map((sub) => (
                        <li
                          key={sub.title}
                          className="flex flex-wrap justify-between gap-x-3 text-sm text-foreground/90"
                        >
                          <span>{sub.title}</span>
                          <span className="text-muted-foreground">
                            {formatRange(sub.startDate, sub.endDate)}
                          </span>
                        </li>
                      ))}
                    </ol>
                  </div>
                ) : null}

                {isEducation ? null : matching.length === 0 ? (
                  <p className="mt-4 text-sm text-muted-foreground italic">
                    Nothing tagged {activeTag ? TAG_LABELS[activeTag].toLowerCase() : ''} in this
                    role.
                  </p>
                ) : (
                  // Keyed on the active filter so React remounts the whole list
                  // rather than reconciling it. AnimatePresence with popLayout
                  // was orphaning exiting nodes here — they stayed in the DOM,
                  // absolutely positioned at opacity 0, so a filter showing 3
                  // matches rendered 5 items. Remounting cannot leave orphans.
                  <ul key={activeTag ?? 'all'} className="mt-4 space-y-2">
                    {visible.map((highlight, idx) => (
                      <motion.li
                        key={highlight.text.slice(0, 48)}
                        initial={reduceMotion ? false : { opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.24,
                          delay: reduceMotion ? 0 : Math.min(idx, 6) * 0.035,
                          ease: 'easeOut',
                        }}
                        className="relative pl-5 text-pretty text-foreground/90"
                      >
                        <span
                          aria-hidden
                          className="absolute top-2.5 left-0 h-1 w-1 rounded-full bg-primary"
                        />
                        {highlight.text}
                      </motion.li>
                    ))}
                  </ul>
                )}

                {!activeTag && hidden > 0 ? (
                  <button
                    type="button"
                    onClick={() => setExpanded((prev) => ({ ...prev, [role.id]: true }))}
                    aria-expanded={false}
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary transition-opacity hover:opacity-75"
                  >
                    <ChevronDown className="h-4 w-4" aria-hidden />
                    Show {hidden} more {hidden === 1 ? 'item' : 'items'}
                  </button>
                ) : null}

                {!activeTag && isOpen && matching.length > COLLAPSED_COUNT ? (
                  <button
                    type="button"
                    onClick={() => setExpanded((prev) => ({ ...prev, [role.id]: false }))}
                    aria-expanded
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <ChevronDown className="h-4 w-4 rotate-180" aria-hidden />
                    Show less
                  </button>
                ) : null}

                <div className="mt-4 flex flex-wrap gap-2">
                  {role.tech.map((tech) => (
                    <Badge key={tech} variant="outline">
                      {tech}
                    </Badge>
                  ))}
                </div>
              </Reveal>
            </li>
          );
        })}
      </ol>
    </Section>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'rounded-full border px-3 py-1.5 text-sm transition-colors',
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}
